import {
    CreateDocumentRequest,
    CreateDocumentResponse,
    MergeRelativeResponse,
    Employee,
    EmployeeList,
    GetDocumentsQuery,
    GetEmployeesWithActivePeriodQuery,
    GetRelativesQuery,
    GetWorkingEventListRequest,
    MergeRelativeRequest,
    ObjectDocumentList,
    relativeDef,
    RelativeListDef,
    StaffClient,
    WorkingEventList,
    MatchEmployeeRequest,
    MatchEmployeeResponse,
    JsonPatchOperation,
    PatchRelativeResponse,
} from "@skbkontur/staff-common";

import { reject } from "../../../Common/TypingUtils";
import { FormSettings } from "../Forms/Types/FormSettings";

export interface IFormStaffClient {
    getStaffClient(): StaffClient;

    getWorkingEventsForEmployee(query: GetWorkingEventListRequest): Promise<WorkingEventList | undefined>;
    getAdditionalEmployeeInfo(query: GetEmployeesWithActivePeriodQuery): Promise<EmployeeList | undefined>;
    getDocuments(query: GetDocumentsQuery, signal?: AbortSignal): Promise<ObjectDocumentList>;
    createDocument(request: CreateDocumentRequest, signal?: AbortSignal): Promise<CreateDocumentResponse>;
    getEmployee(employeeId: string, signal?: AbortSignal): Promise<Employee>;
    getEmployees(query: GetEmployeesWithActivePeriodQuery, signal?: AbortSignal): Promise<EmployeeList>;
    getRelative(employeeId: string, relativeId: string, signal?: AbortSignal): Promise<relativeDef>;
    getRelatives(employeeId: string, query?: GetRelativesQuery, signal?: AbortSignal): Promise<RelativeListDef>;
    mergeRelative(
        employeeId: string,
        query: MergeRelativeRequest,
        signal?: AbortSignal
    ): Promise<MergeRelativeResponse>;
    patchRelative(
        employeeId: string,
        relativeId: string,
        operations: JsonPatchOperation[],
        signal?: AbortSignal
    ): Promise<PatchRelativeResponse>;
    matchEmployee(query: MatchEmployeeRequest, signal?: AbortSignal): Promise<MatchEmployeeResponse>;
}

type FormStaffClientOptions = Pick<FormSettings, "staffServiceUrl" | "employerId">;

export class FormStaffClient implements IFormStaffClient {
    protected readonly staffClient: StaffClient;
    private readonly employerId: string | undefined;

    public constructor(settings: FormStaffClientOptions) {
        this.staffClient = new StaffClient({ url: settings.staffServiceUrl });
        this.employerId = settings.employerId;
    }

    public getStaffClient() {
        return this.staffClient;
    }

    public async getWorkingEventsForEmployee(query: GetWorkingEventListRequest): Promise<WorkingEventList | undefined> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        try {
            const events = await this.staffClient.getWorkingEvents(employerId, query);
            return events;
        } catch {
            return undefined;
        }
    }

    public async getAdditionalEmployeeInfo(
        query: GetEmployeesWithActivePeriodQuery
    ): Promise<EmployeeList | undefined> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        try {
            // интерфейс фильтров устаревший, пацаны из сс должны поправить
            // актуальные тут https://developer.kontur.ru/doc/staff/method?type=get&path=%2Fv1%2Femployers%2F%7BemployerId%7D%2Femployees
            const employee = await this.staffClient.getEmployees(employerId, query);
            return employee;
        } catch {
            return undefined;
        }
    }

    public async getDocuments(query: GetDocumentsQuery, signal?: AbortSignal): Promise<ObjectDocumentList> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.getDocuments(employerId, query, signal);
    }

    public createDocument(request: CreateDocumentRequest, signal?: AbortSignal): Promise<CreateDocumentResponse> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.createDocument(employerId, request, signal);
    }

    public getEmployee(employeeId: string, signal?: AbortSignal): Promise<Employee> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.getEmployee(employerId, employeeId, signal);
    }

    public getEmployees(query: GetEmployeesWithActivePeriodQuery, signal?: AbortSignal): Promise<EmployeeList> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.getEmployees(employerId, query, signal);
    }

    public getRelative(employeeId: string, relativeId: string, signal?: AbortSignal): Promise<relativeDef> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.getRelative(employerId, employeeId, relativeId, signal);
    }

    public getRelatives(employeeId: string, query?: GetRelativesQuery, signal?: AbortSignal): Promise<RelativeListDef> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.getRelatives(employerId, employeeId, query, signal);
    }

    public mergeRelative(
        employeeId: string,
        query: MergeRelativeRequest,
        signal?: AbortSignal
    ): Promise<MergeRelativeResponse> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.mergeRelative(employerId, employeeId, query, signal);
    }

    public patchRelative(
        employeeId: string,
        relativeId: string,
        operations: JsonPatchOperation[],
        signal?: AbortSignal
    ): Promise<PatchRelativeResponse> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.patchRelative(employerId, employeeId, relativeId, operations, signal);
    }

    public matchEmployee(query: MatchEmployeeRequest, signal?: AbortSignal): Promise<MatchEmployeeResponse> {
        const employerId = this.employerId ?? reject("employerId is not defined");
        return this.staffClient.matchEmployee(employerId, query, signal);
    }
}
